/**
 * Fantasy Console 0 - Cart Library Cache
 *
 * In-memory cache for cart library data to prevent unnecessary refetches
 * when switching tabs or changing filters/sort.
 */

import type { CartWithStats } from './cartService'

type SortBy = 'updated' | 'created' | 'title' | 'likes'
type FilterBy = 'all' | 'mine'
type CacheKey = `${FilterBy}:${SortBy}`

interface CacheEntry {
  data: CartWithStats[]
  total: number
  hasMore: boolean
}

// Module-level Map for cache persistence across component remounts
const cache = new Map<CacheKey, CacheEntry>()

// Subscriber pattern for invalidation notifications
const subscribers = new Set<() => void>()

function getCacheKey(filterBy: FilterBy, sortBy: SortBy): CacheKey {
  return `${filterBy}:${sortBy}`
}

export const CartLibraryCache = {
  /**
   * Get cached data for a filter+sort combination
   */
  get(filterBy: FilterBy, sortBy: SortBy): CacheEntry | undefined {
    return cache.get(getCacheKey(filterBy, sortBy))
  },

  /**
   * Set cache data for a filter+sort combination
   */
  set(filterBy: FilterBy, sortBy: SortBy, entry: CacheEntry): void {
    cache.set(getCacheKey(filterBy, sortBy), entry)
  },

  /**
   * Append data for pagination (load more)
   */
  append(
    filterBy: FilterBy,
    sortBy: SortBy,
    result: { data: CartWithStats[]; total: number; hasMore: boolean },
  ): void {
    const key = getCacheKey(filterBy, sortBy)
    const existing = cache.get(key)
    if (existing) {
      cache.set(key, {
        data: [...existing.data, ...result.data],
        total: result.total,
        hasMore: result.hasMore,
      })
    } else {
      cache.set(key, result)
    }
  },

  /**
   * Update a specific cart in all cache entries (for optimistic updates)
   */
  updateCart(
    cartId: string,
    updater: (cart: CartWithStats) => CartWithStats,
  ): void {
    cache.forEach((entry, key) => {
      const updatedData = entry.data.map((cart) =>
        cart.id === cartId ? updater(cart) : cart,
      )
      cache.set(key, { ...entry, data: updatedData })
    })
  },

  /**
   * Invalidate all cache entries and notify subscribers
   */
  invalidateAll(): void {
    cache.clear()
    subscribers.forEach((callback) => callback())
  },

  /**
   * Subscribe to cache invalidation events
   * @returns Unsubscribe function
   */
  subscribe(callback: () => void): () => void {
    subscribers.add(callback)
    return () => {
      subscribers.delete(callback)
    }
  },

  /**
   * Check if cache has data for a filter+sort combination
   */
  has(filterBy: FilterBy, sortBy: SortBy): boolean {
    return cache.has(getCacheKey(filterBy, sortBy))
  },
}
